// ml-card-base.tsx
import classNames from 'classnames';
import React from 'react';
import mlCardVariants from './ml-card.variants';
import {
  AtClickable,
  AtHeader,
  AtIcon,
  AtMedia,
  AtText,
} from '@/components/atoms';
import HomePlanBody from './home-plan-body.client';

import {
  EMlCardsTypes,
  IMlCardProps,
  IDefaultBodyProps,
} from './ml-card.types';

export const MlCardBase = ({
  title,
  body = '',
  iconName,
  imageProps,
  homePlanProps,
  chipProps,
  alignment = 'left',
  variant = EMlCardsTypes.INFOGRAPHIC,
  theme,
  headerElement = 'h4',
  headerVariant = 'h4',
  ctaProps,
  className,
  selected,
  tabIndex,
  ariaLabel,
  showFavouriteButton,
  href,
  onFocus,
}: IMlCardProps) => {
  const shouldRenderIcon =
    (variant === EMlCardsTypes.INFOGRAPHIC ||
      variant === EMlCardsTypes.SIMPLE_ACTION ||
      variant === EMlCardsTypes.ICON) &&
    iconName;

  const shouldRenderImage =
    (variant === EMlCardsTypes.IMAGE ||
      variant === EMlCardsTypes.FULL_IMAGE ||
      variant === EMlCardsTypes.HOME_PLAN ||
      variant === EMlCardsTypes.STAT_CARD) &&
    imageProps;

  const { base, icon, mainImage, chip, contentWrapper } = mlCardVariants({
    type: variant,
    alignment,
    theme,
    selected: variant === EMlCardsTypes.STAT_CARD && selected,
  });

  const renderBody = () => {
    if (variant === EMlCardsTypes.HOME_PLAN && homePlanProps) {
      return (
        <HomePlanBody
          {...homePlanProps}
          headerElement={headerElement}
          showFavouriteButton={showFavouriteButton}
          href={href}
        />
      );
    }

    if (title) {
      return (
        <MlCardBase.DefaultBody
          title={title}
          body={body}
          alignment={alignment}
          variant={variant}
          ctaProps={ctaProps}
          headerElement={headerElement}
          headerVariant={headerVariant}
        />
      );
    }

    return null;
  };

  return (
    <div
      data-testid="ml-card"
      data-theme={theme}
      tabIndex={tabIndex}
      aria-label={ariaLabel}
      onFocus={onFocus}
      className={classNames(base(), 'border-skin-stroke-primary', className)}
    >
      {chipProps && chipProps.icon && (
        <AtText
          className={chip()}
          data-theme="light"
          data-testid="ml-card-chip"
        >
          <AtIcon icon={chipProps.icon} />
          {chipProps.label}
        </AtText>
      )}
      {shouldRenderIcon && (
        <AtIcon
          data-testid="ml-card-main-icon"
          icon={iconName}
          className={icon()}
          color="var(--color-ui-fill-tertiary)"
        />
      )}
      <div className={contentWrapper()}>
        {shouldRenderImage && (
          <div className={mainImage()}>
            <AtMedia
              priority={false}
              src={imageProps.src}
              quality={60}
              alt={imageProps.alt}
              className="rounded-none"
              sizes="(max-width: 767px) 100vw, (max-width: 1023px) 50vw, 33.33vw"
              showOverlay={variant === EMlCardsTypes.FULL_IMAGE}
            />
          </div>
        )}
        {renderBody()}
      </div>
    </div>
  );
};

const DefaultBody = ({
  title,
  variant,
  alignment,
  body,
  headerElement,
  headerVariant,
  ctaProps,
}: IDefaultBodyProps) => {
  const { header, textContainer, bodyContent, cta } = mlCardVariants({
    type: variant,
    alignment,
  });

  return (
    <div className={textContainer()} data-testid="ml-card-default-body">
      {title && (
        <AtHeader
          element={headerElement}
          variant={headerVariant || headerElement}
          className={classNames(header(), { 'mb-6': !!body?.length })}
        >
          {title}
        </AtHeader>
      )}
      {body?.length ? (
        <AtText type="p" className={bodyContent()}>
          {body}
        </AtText>
      ) : null}
      {ctaProps && (
        <AtClickable
          className={classNames(cta(), {
            'mt-1': variant === EMlCardsTypes.IMAGE,
          })}
          {...ctaProps}
        />
      )}
    </div>
  );
};

MlCardBase.DefaultBody = DefaultBody;
